<template>
  <div class="teams-index">
    <LoaderWithText
      v-if="teamsStore.isFetching || !teamsStore.hasFetched"
      class="stk-px-6"
    >
      Loading…
    </LoaderWithText>

    <template v-else>
      <Level
        class="stk-px-6 stk-pb-4"
      >
        <LevelLeft>
          <Title>
            {{ teamsStore.items.length }}
            <span v-if="teamsStore.items.length > 1">Teams</span>
            <span v-else>Team</span>
          </Title>
        </LevelLeft>

        <LevelRight v-if="canCreateTeams">
          <Button
            variant="primary"
            icon-after="fas fa-plus"
            @click="showAddModal = true"
          >
            Create a Team
          </Button>
        </LevelRight>
      </Level>

      <template v-if="teamsStore.items?.length > 0">
        <table class="stk-table">
          <thead class="stk-table-head">
            <tr>
              <th class="stk-table-head-col">
                Name
              </th>
              <th class="stk-table-head-col">
                Description
              </th>
              <th class="stk-table-head-col">
                Owner?
              </th>
              <th class="stk-table-head-col">
                Created
              </th>
              <th
                class="stk-table-head-col"
                colspan="2"
              >
                Updated
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(team) in teamsStore.items"
              :key="team.external_id"
              class="stk-table-row"
            >
              <td class="stk-table-cell cell-name">
                <TextLink
                  v-slot="{ navigate }"
                  class="stk-table-cell-link"
                  :to="{ name: 'team-show', params: { teamExternalId: team.external_id } }"
                >
                  {{ team.name }}
                </TextLink>
              </td>
              <td class="stk-table-cell cell-description">
                {{ team.description }}
              </td>
              <td class="stk-table-cell cell-owner">
                <YesNoBadge :bool="advisorStore.advisor?.id === team.owner?.id" />
              </td>
              <td class="stk-table-cell cell-date">
                {{ formatDate(team.created_at) }}
              </td>
              <td class="stk-table-cell cell-date">
                {{ formatDate(team.updated_at) }}
              </td>
              <td class="stk-table-cell cell-edit">
                <Button
                  size="3"
                  icon-before="fas fa-edit"
                  spacing="tight"
                  :to="{ name: 'team-show', params: { teamExternalId: team.external_id } }"
                >
                  Edit
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <div
        v-else
        class="stk-px-6"
      >
        <Callout
          status="primary"
        >
          <p>No team was found</p>
        </Callout>
      </div>
    </template>

    <StakeModal v-model="showAddModal">
      <TeamForm @onClose="showAddModal = false" />
    </StakeModal>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { formatDate } from '@frontend/shared/Util.js';
import { YesNoBadge, Button, Callout, Level, LevelLeft, LevelRight, LoaderWithText, StakeModal, TextLink, Title } from '@stakeui';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { useTeamsStore } from '@frontend/ui/stores/teamsStore.js';
import TeamForm from '../components/TeamForm.vue';

const advisorStore = useAdvisorStore();
const teamsStore = useTeamsStore();

const showAddModal = ref(false);

const canCreateTeams = computed(() => advisorStore.advisor?.is_firm_admin || advisorStore.advisor?.can_create_teams);

onMounted(() => {
  if (teamsStore.hasFetched) {
    return;
  }

  teamsStore.fetchTeams();
});
</script>

<style scoped>
.cell-name {
  color: var(--stk-text-strong);
  width: 16rem;
  padding: 0 !important;
}

.cell-description {
  width: auto;
}

.cell-owner {
  width: 4rem;
}

.cell-date {
  color: var(--stk-text-normal);
  font-size: 0.75em;
  width: 12rem;
}
</style>
